// Sign-in (D38, D71): the hosted page through GET /v1/auth/sign-in, which comes back to the
// console with the session cookie. Locally, with the dev identity provider, a form signs in as
// any email (POST /v1/auth/dev/session); it is in `vite dev` and in builds with
// VITE_DEV_SIGN_IN=true (the end-to-end suite), never in a production build.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useId, useState } from 'react'
import { API_BASE } from '../api/client'
import { devSignIn, meQuery } from '../api/queries'
import { EntryHead, EntryNote, EntryPage } from '../components/d'
import { useI18n } from '../i18n'
import type { SignInSearch } from './search'

const DEV_SIGN_IN = import.meta.env.DEV || import.meta.env.VITE_DEV_SIGN_IN === 'true'

/** `reauth`: ask for the credentials again, even with a live session (APS-402). */
export function hostedSignInUrl(
  screen: 'sign-in' | 'sign-up',
  returnTo: string,
  reauth = false,
): string {
  const back = `${window.location.origin}${returnTo}`
  return `${API_BASE}/auth/sign-in?screen=${screen}${reauth ? '&reauth=1' : ''}&return_to=${encodeURIComponent(back)}`
}

/** Whether this build signs in with the local form (no hosted page to go back to). */
export const devSignInBuild = DEV_SIGN_IN

export function SignInPage() {
  const { t, locale, rich } = useI18n()
  const search = useSearch({ strict: false }) as SignInSearch
  // Sent by /signup: someone new, so creating an account comes first.
  const signUp = search.screen === 'sign-up'
  const returnTo = search.return_to ?? '/'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const emailId = useId()
  const nameId = useId()
  const me = useQuery({ ...meQuery, throwOnError: false })

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(undefined)
    try {
      await devSignIn(email.trim(), name.trim(), locale)
      await qc.resetQueries({ queryKey: meQuery.queryKey })
      await navigate({ to: returnTo })
    } catch {
      setError(t('signIn.devFailed'))
      setBusy(false)
    }
  }

  // Already signed in: straight on.
  if (me.data && !busy) return <Navigate to={returnTo} />
  const ways: [string, 'sign-in' | 'sign-up'][] = signUp
    ? [
        [t('signIn.signUp'), 'sign-up'],
        [t('signIn.haveAccount'), 'sign-in'],
      ]
    : [
        [t('signIn.continue'), 'sign-in'],
        [t('signIn.signUp'), 'sign-up'],
      ]
  return (
    <EntryPage>
      <EntryHead
        title={signUp ? t('signIn.signUpTitle') : t('signIn.title')}
        lede={t('signIn.lede')}
      />
      {returnTo.startsWith('/invitations/accept') ? (
        // From an invitation's link (APS-274): which address to use, then back to it.
        <EntryNote tone="mute">{t('signIn.invitation')}</EntryNote>
      ) : null}
      {search.error ? (
        // A sign-in the API refused for its address's limit (APS-249): why, and when.
        <EntryNote
          tone="crash"
          title={t(
            search.error === 'network_rate_limited'
              ? 'signIn.limitedAccounts'
              : 'signIn.limitedLogins',
          )}
        >
          {t('signIn.retry', { minutes: search.retry ? Math.ceil(search.retry / 60) : 0 })}
        </EntryNote>
      ) : null}
      <div className="en-acts">
        {ways.map(([label, screen], i) => (
          <a
            key={screen}
            className={i === 0 ? 'btn solid en-full' : 'btn en-full'}
            href={hostedSignInUrl(screen, returnTo)}
          >
            {label}
          </a>
        ))}
      </div>
      {DEV_SIGN_IN ? (
        <form className="en-win en-dev" onSubmit={submit} aria-label={t('signIn.devForm')}>
          <p className="en-dev-k">{t('signIn.devDivider')}</p>
          <div className="en-lbl">
            <label htmlFor={emailId}>{t('signIn.email')}</label>
            <input
              id={emailId}
              className="input en-in"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="en-lbl">
            <label htmlFor={nameId}>{t('signIn.name')}</label>
            <input
              id={nameId}
              className="input en-in"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {error ? <EntryNote tone="crash">{error}</EntryNote> : null}
          <button type="submit" className="btn en-full" disabled={busy}>
            {t('signIn.devSubmit')}
          </button>
          <p className="en-hint">{rich('signIn.devNote')}</p>
        </form>
      ) : null}
    </EntryPage>
  )
}
